/**
 * Reads Apiboo's records into the snapshot graphModel.buildGraph expects.
 *
 * Scopes (27.09.2026, "Graph with team items"): boards, tasks, document
 * folders, documents and calendar events are read from EVERY scope
 * graphScopes.graphRecordScopes() names — the personal scope, the Free plan's
 * device scope, and one `team:<id>` per team on the user's roster (never one
 * found in storage: leaving a team does not purge its rows). Every row is
 * tagged with its wire scope and location, and scopes are concatenated in the
 * fixed order, which is also buildGraph's first-wins collision rule. Projects,
 * their membership links and their code repositories are read from the personal scope and every team
 * scope (team projects, D4); the device scope holds none. The user's own drawn
 * links are personal records (D3), so they arrive with the personal scope.
 *
 * The caller supplies the in-memory sources, and that split is load-bearing:
 *   - the app (graphAppSource.js) passes the live collections/environments
 *     stores and a vault reader, so the Graph page can show DB connections and
 *     SSH hosts;
 *   - MCP (mcp/handlers/graph.js) passes names-only environments, the
 *     workspace-gated collections, and NO vault reader — MCP may never list
 *     vault item names (D4), and this module must not so much as name the
 *     vault commands, because the attack-surface test reads the import graph.
 *
 * Every source is independent: a failing read yields an empty list for that
 * source, never a broken graph, and is reported in `unavailable` so the page
 * (and an agent) can tell "none" from "could not look".
 */
import { listRecords } from '../records/recordsClient';
import { listProjects, listLinks, listRepos } from './projectsStore';
import { graphRecordScopes } from './graphScopes';
import { withoutUser } from './repoRefs';
import { taskStatusOf } from '../extensions/bundled/task-manager/taskStatus';

async function records(scopeKey, extensionId, recordType) {
  const rows = await listRecords({
    workspaceId: scopeKey,
    extensionId,
    recordType,
    scopeId: null,
  });
  return rows || [];
}

// Each family is read per scope, each read failing on its own: one team whose
// rows cannot be read never empties the personal graph or another team's.
const FAMILIES = [
  ['boards', 'boards', 'task-manager', 'board'],
  ['tasks', 'boards', 'task-manager', 'task'],
  ['folders', 'documents', 'documents', 'folder'],
  ['documents', 'documents', 'documents', 'document'],
  ['bodies', 'documents', 'documents', 'docbody'],
  // A body stored per block (docBlocks.js): its page links are read from the
  // blocks — the docbody row then holds only an occasional HTML snapshot.
  ['blocks', 'documents', 'documents', 'docblock'],
  ['events', 'calendar', 'calendar', 'event'],
];

/** Page links in block records -> `[{ docId, bodyHtml }]` with one data-doc-link per link (graphModel.documentLinks). */
function blockPageLinks(rows) {
  const byDoc = new Map();
  const walk = (docId, node) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'page-link' && node.docId) {
      if (!byDoc.has(docId)) byDoc.set(docId, new Set());
      byDoc.get(docId).add(String(node.docId));
    }
    (node.children || []).forEach((c) => walk(docId, c));
  };
  for (const r of rows || []) walk(r.scope_id || r.parent_id, r.node);
  return [...byDoc].map(([docId, ids]) => ({
    docId,
    bodyHtml: [...ids].map((id) => `<a data-doc-link="${id.replace(/"/g, '')}"></a>`).join(''),
  }));
}

/**
 * Which link records may be drawn (review F1, 27.09.2026).
 *
 * A link in the PERSONAL scope is the user's own: drawn as is. A link in a
 * TEAM scope was synced there by any editor of that team, and the app itself
 * only ever writes one kind: `project:<team project> —contains→ <item of that
 * team>` (projectsStore.addToProject). Anything else found in a team scope —
 * a "depends_on" a teammate planted (it would read as the USER's intent, D3),
 * a team project "containing" a personal item (D4), a team row adding to the
 * user's personal project — is dropped. "Of that team" means the node the
 * graph actually shows: the first row for an id wins in scope order, so a team
 * row reusing a personal id does not make that node the team's.
 */
function trustedLinks(links, rows) {
  const owner = new Map(); // node id → wire scope of the row that wins
  const claim = (type, list, idOf = (r) => r.id) => {
    for (const r of list) {
      const id = idOf(r);
      if (id == null || id === '') continue;
      const key = `${type}:${id}`;
      if (!owner.has(key)) owner.set(key, r.scope);
    }
  };
  claim('board', rows.boards);
  claim('task', rows.tasks);
  claim('doc_folder', rows.folders);
  claim('document', rows.documents);
  claim('appointment', rows.appointments, (a) => a.uid ?? a.id);
  claim('project', rows.projects);
  claim('git_repo', rows.repos);
  return links.filter((l) => {
    if (!String(l.scope || '').startsWith('team:')) return true;
    return (
      l.rel === 'contains' &&
      String(l.from || '').startsWith('project:') &&
      owner.get(l.from) === l.scope &&
      owner.get(l.to) === l.scope
    );
  });
}

/**
 * @param {object} sources
 * @param {Array} [sources.collections]   live collection tree (requests inside)
 * @param {Array} [sources.environments]  [{ id, name, variables:[{ key }] }] — only keys are read
 * @param {Function|null} [sources.readVault] resolves to vault envelopes, or null when locked;
 *                                         omitted → no vault-backed nodes and nothing reported
 */
export async function readGraphSnapshot({ collections = [], environments = [], readVault = null } = {}) {
  const unavailable = [];
  const safely = async (source, fn, fallback = []) => {
    try {
      return await fn();
    } catch (err) {
      unavailable.push(source);
      if (process.env.NODE_ENV !== 'test') console.warn(`[graph] ${source} unavailable:`, err);
      return fallback;
    }
  };

  const { scopes, unavailable: scopeGaps } = graphRecordScopes();
  unavailable.push(...scopeGaps);
  const perScope = (family, source, extensionId, recordType) =>
    Promise.all(
      scopes.map(async (s) => {
        const rows = await safely(source, () => records(s.scopeKey, extensionId, recordType));
        return rows.map((row) => ({ ...row, scope: s.wire, location: s.location }));
      })
    ).then((lists) => [family, lists.flat()]);

  const projectScopes = scopes.filter((s) => s.location !== 'device');
  const projectRows = (read, tag) =>
    Promise.all(
      projectScopes.map(async (s) => (await safely('projects', () => read(s.scopeKey))).map((row) => tag(row, s)))
    ).then((lists) => lists.flat());

  const [families, vault, projects, links, repos] = await Promise.all([
    Promise.all(FAMILIES.map((f) => perScope(...f))).then(Object.fromEntries),
    readVault ? safely('vault', readVault, null) : Promise.resolve([]),
    projectRows(listProjects, (p, s) => ({ ...p, scope: s.wire, location: s.location })),
    projectRows(listLinks, (l, s) => ({ ...l, scope: s.wire })),
    projectRows(listRepos, (r, s) => ({ ...r, scope: s.wire, location: s.location })),
  ]);
  const { boards, tasks, folders, documents, bodies, blocks, events } = families;
  if (readVault && vault === null && !unavailable.includes('vault')) unavailable.push('vault');
  const where = (r) => ({ scope: r.scope, location: r.location });
  // A root row of the cloud-personal bucket may name a document workspace
  // (card 5.4); the graph groups those roots under it (graphDocWorkspaces.js).
  const ownWorkspace = (r) => (r.scope === 'personal' && r.location !== 'device' && r.scopeId ? { scopeId: String(r.scopeId) } : {});
  const personal = scopes.find((s) => s.wire === 'personal' && s.location !== 'device');
  const docWorkspaces = personal
    ? (await safely('documents', () => records(personal.scopeKey, 'documents', 'docscope'))).map((r) => ({ id: r.id, name: r.name }))
    : [];
  const appointments = events.filter((e) => !e.override_of && e.status !== 'cancelled');

  return {
    unavailable: [...new Set(unavailable)],
    snapshot: {
      collections,
      environments,
      boards: boards.map((b) => ({ id: b.id, name: b.name, ...where(b) })),
      tasks: tasks.map((t) => ({ id: t.id, title: t.title, scope_id: t.scope_id, done: t.done === true, status: taskStatusOf(t), ...where(t) })),
      docNodes: [
        ...folders.map((r) => ({ id: r.id, type: 'folder', name: r.name, parentId: r.parent_id ?? null, ...where(r), ...ownWorkspace(r) })),
        ...documents.map((r) => ({ id: r.id, type: 'document', name: r.title, parentId: r.parent_id ?? null, ...where(r), ...ownWorkspace(r) })),
      ],
      docWorkspaces,
      docBodies: [
        ...bodies.map((r) => ({ docId: r.scope_id || r.parent_id, bodyHtml: r.bodyHtml })),
        ...blockPageLinks(blocks),
      ],
      // Master records only: a recurring meeting is one node, not one per week.
      appointments,
      dbConnections: (vault || [])
        .filter((it) => it.type === 'dbConnection')
        .map((it) => ({ id: it.id, name: it.name, engine: it.index?.engine })),
      sshHosts: (vault || []).filter((it) => it.type === 'sftp').map((it) => ({ id: it.id, name: it.name })),
      projects,
      // Code repositories linked to projects: name, host and the remote (stored
      // without credentials) — never a local clone path, which names this
      // device's folders.
      repos: repos.map((r) => ({ id: r.id, name: r.name, provider: r.provider, host: r.host, remote: withoutUser(r.remote), ...where(r) })),
      links: trustedLinks(links, { boards, tasks, folders, documents, appointments, projects, repos }),
    },
  };
}
